import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, XCircle, AlertTriangle, Building2 } from 'lucide-react';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Spinner } from '@/shared/ui/Spinner';
import { ApiException } from '@/shared/api/client';
import { formatEur, formatDate } from '@/lib/utils';
import { useApprovalView, useDecide } from './approvals.api';
import { usePwaUpdate } from '@/shared/pwa/usePwaUpdate';

export function ApprovalPage() {
  const { token = '' } = useParams();
  const { data, isLoading, error, refetch } = useApprovalView(token);
  const decide = useDecide(token);

  const [decidedByName, setDecidedByName] = useState('');
  const [comment, setComment] = useState('');
  const [rejectMode, setRejectMode] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  /**
   * Update-Behandlung auf der Freigabeseite (ADR-033). Dieselbe Drei-Zustände-
   * Logik wie im Erfassungsdialog, aus demselben Grund - und hier wiegt sie
   * schwerer: Der Kunde trifft eine UNWIDERRUFLICHE Entscheidung (ADR-019),
   * und anders als beim eigenen Mitarbeiter kann ihm niemand einen Handgriff
   * am Gerät erklären, wenn seine App festhängt.
   *
   *  1. Unberührt  -> sofort anwenden (frisch geöffneter Link, nichts getippt).
   *  2. In Arbeit  -> NICHTS tun, NICHTS einblenden. Ein Neuladen verlöre den
   *     eingetippten Namen und den Kommentar, der bei Ablehnung Pflicht ist.
   *  3. Entschieden -> anwenden, die Entscheidung ist beim Server.
   */
  const { updateVerfuegbar, anwenden } = usePwaUpdate();
  const inArbeit =
    done === null &&
    (decidedByName.trim() !== '' || comment.trim() !== '' || rejectMode || decide.isPending);

  useEffect(() => {
    if (!updateVerfuegbar) return;
    if (done !== null || !inArbeit) anwenden();
  }, [updateVerfuegbar, done, inArbeit, anwenden]);

  if (isLoading) return <Spinner />;
  if (error || !data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
        <div className="max-w-sm rounded-xl bg-white p-6 text-center shadow-sm">
          <AlertTriangle className="mx-auto h-10 w-10 text-amber-500" />
          <h1 className="mt-3 text-lg font-semibold">Link nicht gültig</h1>
          <p className="mt-1 text-sm text-slate-600">
            {error instanceof ApiException
              ? error.message
              : 'Bitte wenden Sie sich an Ihren Dienstleister.'}
          </p>
        </div>
      </div>
    );
  }

  const act = async (decision: 'CONFIRMED' | 'REJECTED') => {
    setSubmitError(null);
    if (decision === 'REJECTED' && !comment.trim()) {
      setSubmitError('Bitte teilen Sie uns kurz mit, warum Sie ablehnen.');
      return;
    }
    try {
      const res = await decide.mutateAsync({
        decision,
        ...(comment.trim() ? { comment: comment.trim() } : {}),
        ...(decidedByName.trim() ? { decidedByName: decidedByName.trim() } : {}),
      });
      setDone(res.message);
      void refetch();
    } catch (e) {
      setSubmitError(
        e instanceof ApiException ? e.message : 'Senden fehlgeschlagen. Bitte erneut versuchen.',
      );
    }
  };

  const before = data.photos.filter((p) => p.phase === 'BEFORE');
  const after = data.photos.filter((p) => p.phase === 'AFTER');
  const other = data.photos.filter((p) => p.phase === 'OTHER');
  const decided = !data.canDecide || !!done;

  return (
    <div className="min-h-screen bg-slate-50 pb-10">
      <header className="border-b border-slate-200 bg-white px-4 py-4">
        <div className="mx-auto max-w-2xl">
          <p className="flex items-center gap-1.5 text-sm text-slate-500">
            <Building2 className="h-4 w-4" /> {data.companyName}
          </p>
          <h1 className="mt-1 text-xl font-bold text-slate-900">Leistungsnachweis</h1>
          <p className="text-sm text-slate-600">
            {data.projectTitle} · {data.objectName}
          </p>
        </div>
      </header>

      <main className="mx-auto max-w-2xl space-y-5 p-4">
        {done && (
          <div className="flex items-start gap-2 rounded-xl border border-green-200 bg-green-50 p-4 text-green-800">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
            <p className="text-sm">{done}</p>
          </div>
        )}
        {!done && !data.canDecide && (
          <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
            Sie haben am {formatDate(data.decidedAt)} entschieden:{' '}
            <strong>{data.decision === 'CONFIRMED' ? 'Bestätigt' : 'Abgelehnt'}</strong>
            {data.comment && <p className="mt-1 italic">„{data.comment}"</p>}
          </div>
        )}

        <section className="rounded-xl bg-white p-4 shadow-sm">
          <h2 className="mb-2 font-semibold text-slate-900">Zusammenfassung</h2>
          <p className="text-sm text-slate-800">{data.summary}</p>
          <h3 className="mb-1 mt-4 text-sm font-semibold text-slate-700">Ausgeführte Arbeiten</h3>
          <p className="whitespace-pre-wrap text-sm text-slate-800">{data.workDescription}</p>
          {data.scopeOriginal && (
            <p className="mt-3 rounded-lg bg-slate-50 p-2 text-xs text-slate-500">
              Vereinbarter Leistungsumfang: {data.scopeOriginal}
            </p>
          )}
        </section>

        <section className="rounded-xl bg-white p-4 shadow-sm">
          <h2 className="mb-3 font-semibold text-slate-900">Zusätzliche Leistungen</h2>
          <ul className="space-y-3">
            {data.extraServices.map((e) => (
              <li key={e.id} className="rounded-lg border border-slate-200 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-slate-900">{e.title}</p>
                    {e.description && <p className="text-sm text-slate-600">{e.description}</p>}
                    {e.reason && (
                      <p className="mt-1 text-xs text-slate-500">Grund: {e.reason}</p>
                    )}
                  </div>
                  <span className="shrink-0 font-semibold text-slate-900">
                    {formatEur(e.netAmount)}
                  </span>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-3 flex items-center justify-between border-t border-slate-200 pt-3">
            <span className="text-sm text-slate-600">Summe (netto)</span>
            <span className="text-lg font-bold text-slate-900">{formatEur(data.totalNet)}</span>
          </div>
          <p className="mt-1 text-xs text-slate-400">zzgl. gesetzlicher Umsatzsteuer</p>
        </section>

        {data.photos.length > 0 && (
          <section className="rounded-xl bg-white p-4 shadow-sm">
            <h2 className="mb-3 font-semibold text-slate-900">Fotodokumentation</h2>
            {[
              { label: 'Vorher', list: before },
              { label: 'Nachher', list: after },
              { label: 'Weitere', list: other },
            ]
              .filter((g) => g.list.length > 0)
              .map((g) => (
                <div key={g.label} className="mb-3">
                  <h3 className="mb-1 text-sm font-semibold text-slate-700">{g.label}</h3>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {g.list.map((p, i) => (
                      <a key={i} href={p.url} target="_blank" rel="noreferrer">
                        <img src={p.url} alt="" className="h-32 w-full rounded-lg object-cover" />
                      </a>
                    ))}
                  </div>
                </div>
              ))}
          </section>
        )}

        {!decided && (
          <section className="rounded-xl bg-white p-4 shadow-sm">
            <h2 className="mb-3 font-semibold text-slate-900">Ihre Entscheidung</h2>
            <Input
              label="Ihr Name (optional)"
              value={decidedByName}
              onChange={(e) => setDecidedByName(e.target.value)}
              placeholder="Für den Nachweis"
            />
            <div className="mt-3 space-y-1">
              <label className="block text-sm font-medium text-slate-700">
                Kommentar {rejectMode ? '*' : '(optional)'}
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={2}
                placeholder={rejectMode ? 'Warum lehnen Sie ab?' : 'Anmerkung'}
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>
            {submitError && (
              <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                {submitError}
              </p>
            )}
            <p className="mt-3 text-xs text-slate-500">
              Ihre Entscheidung ist verbindlich und kann nicht geändert werden.
            </p>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              {!rejectMode ? (
                <>
                  <Button
                    size="lg"
                    className="flex-1"
                    onClick={() => act('CONFIRMED')}
                    loading={decide.isPending}
                  >
                    <CheckCircle2 className="h-5 w-5" /> Bestätigen
                  </Button>
                  <Button size="lg" variant="secondary" className="flex-1" onClick={() => setRejectMode(true)}>
                    <XCircle className="h-5 w-5" /> Ablehnen
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    size="lg"
                    variant="danger"
                    className="flex-1"
                    onClick={() => act('REJECTED')}
                    loading={decide.isPending}
                  >
                    <XCircle className="h-5 w-5" /> Ablehnung senden
                  </Button>
                  <Button size="lg" variant="secondary" onClick={() => setRejectMode(false)}>
                    Zurück
                  </Button>
                </>
              )}
            </div>
          </section>
        )}

        <p className="text-center text-xs text-slate-400">
          Gültig bis {formatDate(data.expiresAt)} · Bereitgestellt über ProofFlow
        </p>
      </main>
    </div>
  );
}
